import React, { useEffect } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import Header from "../../components/layout/header";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import styled from "styled-components";
import Back from "../../components/layout/back";
import {
  ActiveArea,
  ContentArea,
  HeaderArea,
  Main,
  Title,
  TitleArea,
  VerticalContent,
} from "../../styles/shared/page";
import ModalHandler from "../modals/modal-handler";
import Toastr from "../../components/notification/toastr";
import { useErrorDisplay } from "../../hooks/useErrorDisplay";
import Button from "../../components/controls/button";
import { createOrder, validateBasket } from "../../store/features/ordering/order-slice";
import { fetchBasketTotals } from "../../store/features/ordering/basket/basket-slice";
import { Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ApplicationOrder } from "../../models/order/application-order";
import ConfirmModal from "../../components/notification/confirm-modal";
import DrawerHandler from "../drawer/drawer-handler";
import { palletFormatter } from "../../helpers/formatters";

interface LayoutProps {
  children: React.ReactNode;
}

const BasketLayout: React.FC<LayoutProps> = ({ children }) => {
  const user = useAppSelector((state) => state.user).user;
  const branch = useAppSelector((state) => state.branch).selectedBranch;
  const basketState = useAppSelector((state) => state.basket);
  const basket = basketState.basket;
  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useErrorDisplay();

  const createOrderFromBasket = async () => {
    if (basket?.id && basket.itemCount > 0) {
      const latestTotals = await dispatch(fetchBasketTotals(basket.id));
      const totals = fetchBasketTotals.fulfilled.match(latestTotals) ? latestTotals.payload : null;
      if (totals?.suppliers.some(supplier => supplier.requiredMinimumPallets != null && !supplier.isPalletMinimumMet)) return;

      const validation = await dispatch(validateBasket(basket.id));
      if (!validateBasket.fulfilled.match(validation)) return;
      if (validation.payload?.length) return;

      const createdOrder = await dispatch(createOrder(basket.id));
      const orders = createdOrder.payload as ApplicationOrder[];

      if (orders?.length > 0) {
        navigate(`/order/${orders[0]!.id}/confirmation`);

        if (orders.length > 1) {
          orders.slice(1).forEach(o => {
            window.open(`/order/${o.id}/confirmation`, '_blank');
          });
        }
      }
    }
  };


  return (
    <div className="layout">
      <Toastr></Toastr>
      <ModalHandler></ModalHandler>
      <DrawerHandler></DrawerHandler>
      <ConfirmModal></ConfirmModal>
      <Header name={user?.name} team={user?.role} selectedBranch={branch} />
      <VerticalContent>
        <Back></Back>
        <HeaderArea>
          <TitleArea>
            <Title>
              Basket ({basket?.itemCount})
              {(basket?.id && basket.itemCount > 0) && (
                <Button
                  id="btnCompleteOrder"
                  label="complete order"
                  variant="contained"
                  onClick={createOrderFromBasket}
                  loading={order.loading}
                  disabled={basketState.basketTotals?.suppliers.some(supplier => supplier.requiredMinimumPallets != null && !supplier.isPalletMinimumMet) ?? false}
                  fullWidth={false}
                ></Button>
              )}
            </Title>
            <Buttons></Buttons>
          </TitleArea>
        </HeaderArea>
        <Main>
          <ContentArea>
            {order.errors?.map((message, index) => (
              <Alert key={`alert_${index}`} severity="error"> {message}</Alert>
            ))}
            {basketState.basketTotals?.suppliers.filter(supplier => supplier.requiredMinimumPallets != null && !supplier.isPalletMinimumMet).map(supplier => (
              <Alert key={`pallet-order-${supplier.supplierId}`} severity="warning">
                {supplier.supplierName} requires {palletFormatter(supplier.requiredMinimumPallets ?? 0)} pallets. Your basket contains {palletFormatter(supplier.currentTotalPalletEquivalent ?? 0)} pallets; add {palletFormatter(supplier.palletShortfall ?? 0)} pallets before placing the order.
              </Alert>
            ))}

            <ActiveArea>{children}</ActiveArea>
          </ContentArea>
        </Main>
      </VerticalContent>
    </div>
  );
};

export default BasketLayout;

const Buttons = styled.div`
  button {
    margin-right: 30px;
  }
`;
